// AgentContainmentPanel — read-only V1 view of the layered runtime containment
// that keeps a sandboxed agent contained: network L4 (brace) ∩ L7 (warden),
// kernel, sensitive files, host IMDS posture (read live), and MCP governance.
// Lives under "Agent hooks" on the Policy page. Self-fetches /api/fleet/containment.
import { useEffect, useState } from "react";
import { Card, Tag, Tooltip, Popover, Input, Button, Segmented, Checkbox, Switch, theme } from "antd";
import { Network, ShieldCheck, Cpu, FileLock2, Server, Plug, CircleCheck, CircleAlert, CircleDot, Info, Plus, X, type LucideIcon } from "lucide-react";
import { fleetApi, type Containment, type ControlStatus } from "@/lib/api/fleet";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PolicyContent = any;
type Updater = (fn: (draft: PolicyContent) => void) => void;
// resolved control status — the base ControlStatus plus the live/derived states
type Stat = ControlStatus | "hardened" | "exposed" | "off" | "observing";

// hover dot carrying the technical detail — keeps the main copy plain for execs.
// Popover, not Tooltip: every caller's text is a full explanatory sentence
// (see SYSCALL_INFO / LAYERS' `detail` fields below), not a short label.
function InfoDot({ text }: { text: string }) {
  return (
    <Popover content={<div style={{ maxWidth: 320, fontSize: 11.5 }}>{text}</div>}>
      <Info size={13} className="inline-flex flex-none cursor-help text-text-muted opacity-60 hover:opacity-100" />
    </Popover>
  );
}

// ── inline policy editors (shown when the policy is in edit mode) ─────────────
// Plain-English explanation of each low-level system call, for the hover icon.
const SYSCALL_INFO: Record<string, string> = {
  exec: "execve/execveat: every new program the agent launches. Observe only.",
  open: "open/openat: files the agent reads or writes. Observe only.",
  socket: "socket(): when the agent creates a network socket. Observe only.",
  connect: "connect(): outbound connection attempts. Observe only.",
  delete: "unlink/unlinkat/rmdir: files or directories the agent removes. Observe only.",
  mmap_exec: "mmap+PROT_EXEC / memfd: marking memory runnable — how fileless malware runs code that never touches disk. Can block.",
  suspicious_syscalls: "unshare(2): creating new namespaces, the primitive for escaping the container. ptrace(2): reading or injecting another process's memory. Both are monitored the moment this is on; exempt a trusted workload in the JSON with allow_unshare / allow_ptrace. Can block.",
};
const SYS_LIST: { k: string; canBlock: boolean }[] = [
  { k: "exec", canBlock: false }, { k: "open", canBlock: false }, { k: "socket", canBlock: false },
  { k: "connect", canBlock: false }, { k: "delete", canBlock: false },
  { k: "mmap_exec", canBlock: true }, { k: "suspicious_syscalls", canBlock: true },
];

// Sensitive-files editor — the fully-editable exemplar. Matches the policy
// schema exactly: mode (off/alert/block via enable+remediate), paths with a
// per-path write-only flag, and program+file exceptions that bypass the deny.
type SfaPath = { path: string; write_only?: boolean };
type SfaExc = { program: string; file: string };

function SensitiveFilesEditor({ content, update }: { content: PolicyContent; update: Updater }) {
  const sfa = content?.brace?.sensitive_file_access;
  const paths: SfaPath[] = sfa?.paths ?? [];
  const excs: SfaExc[] = sfa?.exceptions ?? [];
  const mode = !sfa?.enable ? "off" : sfa.remediate ? "block" : "alert";
  const ensure = (d: PolicyContent) => ((d.brace ??= {}).sensitive_file_access ??= { enable: true, paths: [] });

  const setMode = (m: string) => update((d) => { const s = ensure(d); if (m === "off") s.enable = false; else { s.enable = true; s.remediate = m === "block"; } });

  const [newPath, setNewPath] = useState("");
  const addPath = () => { const p = newPath.trim(); if (!p) return; update((d) => { const s = ensure(d); s.enable = true; (s.paths ??= []).push({ path: p }); }); setNewPath(""); };
  const editPath = (i: number, v: string) => update((d) => { const s = d.brace?.sensitive_file_access; if (s?.paths?.[i]) s.paths[i].path = v; });
  const toggleWO = (i: number, v: boolean) => update((d) => { const s = d.brace?.sensitive_file_access; if (s?.paths?.[i]) s.paths[i].write_only = v; });
  const removePath = (i: number) => update((d) => { const s = d.brace?.sensitive_file_access; if (s?.paths) s.paths.splice(i, 1); });

  const addExc = () => update((d) => { const s = ensure(d); (s.exceptions ??= []).push({ program: "", file: "" }); });
  const setExc = (i: number, k: "program" | "file", v: string) => update((d) => { const s = d.brace?.sensitive_file_access; if (s?.exceptions?.[i]) s.exceptions[i][k] = v; });
  const removeExc = (i: number) => update((d) => { const s = d.brace?.sensitive_file_access; if (s?.exceptions) s.exceptions.splice(i, 1); });

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-md border border-border bg-surface-elev p-3">
      {/* mode */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Mode</span>
        <Segmented size="small" value={mode} onChange={(v) => setMode(String(v))}
          options={[{ label: "Off", value: "off" }, { label: "Alert", value: "alert" }, { label: "Block", value: "block" }]} />
        <span className="text-[11px] text-text-muted">{mode === "off" ? "not monitored" : mode === "block" ? "reads/writes blocked" : "logged, not blocked"}</span>
      </div>

      {/* paths */}
      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Sensitive paths</div>
        <div className="flex flex-col gap-1.5">
          {paths.length === 0 && <span className="text-xs text-text-muted">No paths yet — add the credential files to guard.</span>}
          {paths.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input size="small" value={p.path} onChange={(e) => editPath(i, e.target.value)} style={{ maxWidth: 320, fontFamily: MONO }} />
              <Checkbox checked={!!p.write_only} onChange={(e) => toggleWO(i, e.target.checked)}>
                <span className="text-[11.5px] text-text-secondary">writes only</span>
              </Checkbox>
              <Popover content={<div style={{ maxWidth: 260 }}>When on, only writes to this path fire — routine reads are ignored.</div>}>
                <Info size={12} className="cursor-help text-text-muted opacity-60" />
              </Popover>
              <button type="button" aria-label="Remove path" onClick={() => removePath(i)} className="ml-auto text-text-muted hover:text-danger"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <Input size="small" placeholder="**/.aws/credentials" value={newPath} onChange={(e) => setNewPath(e.target.value)} onPressEnter={addPath} style={{ maxWidth: 320, fontFamily: MONO }} />
          <Button size="small" icon={<Plus size={13} />} onClick={addPath}>Add path</Button>
        </div>
      </div>

      {/* exceptions */}
      <div>
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Exceptions</span>
          <span className="text-[11px] text-text-muted">optional — a program allowed to touch these files without alerting</span>
        </div>
        <div className="flex flex-col gap-1.5">
          {excs.map((x, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input size="small" prefix={<span className="text-[10px] text-text-muted">prog</span>} placeholder="/usr/sbin/sshd" value={x.program} onChange={(e) => setExc(i, "program", e.target.value)} style={{ maxWidth: 240, fontFamily: MONO }} />
              <Input size="small" prefix={<span className="text-[10px] text-text-muted">file</span>} placeholder="**/.ssh/*" value={x.file} onChange={(e) => setExc(i, "file", e.target.value)} style={{ maxWidth: 220, fontFamily: MONO }} />
              <button type="button" aria-label="Remove exception" onClick={() => removeExc(i)} className="ml-auto text-text-muted hover:text-danger"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Button size="small" icon={<Plus size={13} />} onClick={addExc}>Add exception</Button>
        </div>
      </div>
    </div>
  );
}

function SyscallEditor({ content, update }: { content: PolicyContent; update: Updater }) {
  const sys = content?.brace?.syscalls;
  const stateOf = (k: string): string => { const o = sys?.[k]; if (!o?.enable) return "off"; return o.remediate ? "block" : "observe"; };
  const setOf = (k: string, next: string) => update((d) => {
    const sc = ((d.brace ??= {}).syscalls ??= { enable: true }); sc.enable = true;
    const cur = (sc[k] ??= {});
    if (next === "off") cur.enable = false; else { cur.enable = true; cur.remediate = next === "block"; }
  });
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {SYS_LIST.map(({ k, canBlock }) => (
        <div key={k} className="flex items-center gap-2">
          <span style={{ fontFamily: MONO }} className="text-[12px] text-text-primary">{k}</span>
          <InfoDot text={SYSCALL_INFO[k]} />
          <Segmented size="small" className="ml-auto" value={stateOf(k)} onChange={(v) => setOf(k, String(v))}
            options={canBlock ? ["off", "observe", "block"] : ["off", "observe"]} />
        </div>
      ))}
    </div>
  );
}

const LAYER_ICON: Record<string, LucideIcon> = {
  "net-l4": Network, "net-l7": ShieldCheck, kernel: Cpu, files: FileLock2, imds: Server, mcp: Plug,
};

// representative fallback (mirrors the fleetd model) so the panel renders offline
const FALLBACK: Containment = {
  summary: { enforcing: 4, available: 6, attention: 0, partial: 0 },
  imds: [{ host: "rc24-policy-test", http_tokens: "optional", hop_limit: 2, endpoint: "enabled", status: "exposed" }],
  layers: [
    { id: "net-l4", name: "Network access", engine: "network", plain: "What the agent can reach on the internet. Nothing leaves unless you allow it.", detail: "brace default-deny nftables egress by IP/CIDR.", controls: [
      { name: "Approved destinations only", plain: "The agent can only reach places you approve (e.g. the Anthropic API).", detail: "brace default-deny; per-policy allow_to CIDR list.", status: "enforcing" },
      { name: "Block internal & cloud-key addresses", plain: "Stops the agent reaching your internal systems or the cloud's credential endpoint.", detail: "Blocks 169.254.0.0/16 (metadata) + private ranges.", status: "enforcing" }] },
    { id: "net-l7", name: "Request inspection", engine: "network", plain: "Looks inside the agent's actual web requests, even encrypted ones, and enforces what's allowed.", detail: "Delivered by warden, a per-sandbox HTTPS-inspecting proxy. Effective egress = L4 ∩ L7.", controls: [
      { name: "Approved AI models only", plain: "The agent can only talk to the AI models you approve, so it can't quietly switch to another provider.", detail: "warden reads the LLM request body and allows only pinned models.", status: "available" }] },
    { id: "kernel", name: "Sandbox & host integrity", engine: "kernel_sensor", plain: "Watches low-level kernel activity for attempts to break out of the sandbox or run hidden code.", detail: "In-kernel (libgyro) detection and block.", controls: [
      { name: "Block sandbox escape & injection", plain: "Stops the two kernel moves used to break out of the container or reach into another process.", detail: "suspicious_syscalls: unshare(2) namespace escape and ptrace(2) process injection. Both are monitored the moment this is on; exempt a trusted workload in the JSON with allow_unshare / allow_ptrace.", status: "enforcing" },
      { name: "Block fileless code execution", plain: "Stops code that runs straight from memory without ever being written to disk.", detail: "mmap_exec: executable memory mappings (mmap+PROT_EXEC), memfd-backed exec, suspicious .so loads.", status: "enforcing" },
      { name: "Watch key syscalls", plain: "Logs the agent's low-level activity for detection and audit. Visibility only, no blocking.", detail: "Observers: exec (execve), open (file opens), socket, connect (outbound), delete (unlink/rmdir).", status: "available" }] },
    { id: "files", name: "Secrets on disk", engine: "sensitive_file_access", plain: "Guards stored credentials from being read by the agent. Only preapproved processes can access the files on this list.", detail: "Watch/alert/block reads on any path.", controls: [
      { name: "Guard credential files", plain: "Alerts or blocks when the agent reads stored secrets: cloud keys, Kubernetes tokens, and more.", detail: "~/.aws, GCP creds, /var/run/secrets/**/token.", status: "enforcing" }] },
    { id: "imds", name: "Cloud credentials (metadata)", engine: "IMDS", plain: "Every cloud server has a local address (169.254.169.254) that hands out its temporary cloud keys, a top target for credential theft. BlueRock keeps sandboxes from reaching it.", detail: "169.254.169.254 — the link-local metadata endpoint.", controls: [
      { name: "Block at the network layer", plain: "The egress firewall drops the metadata IP range, so a sandbox can't route to it.", detail: "169.254.0.0/16 dropped by the brace default-deny egress (part of Network access).", status: "enforcing" }] },
    { id: "mcp", name: "Agent tools (MCP)", engine: "MCP filters", plain: "Controls the tools the agent can use and how they run.", detail: "Tool-arg filtering + transport hardening.", controls: [
      { name: "Filter tool inputs", plain: "Blocks tool inputs aimed at internal or cloud-key addresses.", detail: "tool_arg_filter: 169.254.*, RFC1918, file://, localhost.", status: "available" },
      { name: "Keep tools local", plain: "Runs tools locally instead of as remote web servers, removing a whole class of remote attack.", detail: "stdio transport; no remote-HTTP MCP-server SSRF surface.", status: "available" }] },
  ],
};

// containment layer id → the Policy surface whose real editor we embed inline.
// files + kernel are edited by the panel's own compact editors (above); network
// + mcp reuse their full dedicated editors so there's a single source of truth.
const LAYER_SURFACE: Record<string, string> = { "net-l4": "network", "net-l7": "models", mcp: "mcp" };

export default function AgentContainmentPanel({ editing, content, update, renderEditor, collapsed }: { editing?: boolean; content?: PolicyContent; update?: Updater; renderEditor?: (key: string) => React.ReactNode; collapsed?: boolean } = {}) {
  const { token } = theme.useToken();
  const [data, setData] = useState<Containment | null>(null);
  useEffect(() => { fleetApi.containment().then(setData).catch(() => setData(FALLBACK)); }, []);
  const c = data ?? FALLBACK;

  // one-click "Block IMDS" toggle, bound to brace.network.enable. brace.network is
  // allowlist-only (no deny list), so default-deny is the single lever that drops
  // the metadata IP (169.254.*) — this is the same toggle as Network access, made
  // dead simple right where the risk is described.
  const imdsBlockToggle = (layerId: string, ctrlName: string): React.ReactNode => {
    if (!editing || !content || !update) return null;
    if (layerId !== "imds" || !ctrlName.startsWith("Block at the network")) return null;
    const on = !!content.brace?.network?.enable;
    const hasAllow = (content.brace?.network?.allow_to?.length ?? 0) > 0;
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Switch size="small" checked={on} onChange={(v) => update((d) => { ((d.brace ??= {}).network ??= { enable: false, allow_to: [] }).enable = v; })} />
        <span className="text-xs text-text-secondary">{on ? "Blocked — the metadata IP is dropped by the egress firewall." : "Off — turn on to block the metadata IP (169.254.169.254)."}</span>
        {on && !hasAllow && (
          <span className="text-[11px]" style={{ color: token.colorWarning }}>Add allowed destinations in Network access so approved APIs still work.</span>
        )}
      </div>
    );
  };

  // layer-level editor. When the policy is in edit mode each layer shows its real
  // editor: network/mcp reuse the dedicated surface JSX (renderEditor); files +
  // kernel use the panel's compact editors. Single source of truth, no drift.
  const layerEditor = (layerId: string): React.ReactNode => {
    if (!editing) return null;
    if (layerId === "files" && content && update) return <div className="border-t border-border px-4 py-3"><SensitiveFilesEditor content={content} update={update} /></div>;
    if (layerId === "kernel" && content && update) return <div className="border-t border-border px-4 py-3"><SyscallEditor content={content} update={update} /></div>;
    if (renderEditor && LAYER_SURFACE[layerId]) return <div className="border-t border-border">{renderEditor(LAYER_SURFACE[layerId])}</div>;
    return null;
  };

  // ── real status, derived from the actual policy content (not hardcoded) ──
  // A control's tag must reflect what the policy actually does for this scope.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ruleMode = (r: any): Stat => (!r?.enable ? "off" : r.remediate ? "enforcing" : "observing");
  const resolveStatus = (layerId: string, ctrlName: string, fallback: ControlStatus): Stat => {
    if (!content) return fallback;
    const b = content.brace ?? {};
    const sc = b.syscalls;
    switch (layerId) {
      case "net-l4":
        return b.network?.enable ? "enforcing" : "off";
      case "net-l7":
        // "Approved AI models only" is warden.model_pin (a real policy field);
        // the SSRF guard + L7 inspection are warden-proxy features with no policy
        // knob yet, so they stay at their capability status.
        if (ctrlName.startsWith("Approved AI models")) {
          const m = content.models;
          return !m?.models?.length ? "off" : m.on_violation === "alert" ? "observing" : "enforcing";
        }
        return fallback;
      case "files":
        return ruleMode(b.sensitive_file_access);
      case "kernel":
        if (!sc?.enable) return "off";
        if (ctrlName.startsWith("Watch")) return ["exec", "open", "socket", "connect", "delete"].some((k) => sc?.[k]?.enable) ? "observing" : "off";
        if (ctrlName.includes("fileless")) return ruleMode(sc?.mmap_exec);
        return ruleMode(sc?.suspicious_syscalls); // "Block sandbox escape & injection"
      case "imds":
        // the only IMDS control is the network-layer block (tracks the egress firewall)
        return b.network?.enable ? "enforcing" : "off";
      default:
        return fallback; // net-l7 (warden capability), mcp
    }
  };

  // tally the resolved statuses so the header summary matches the visible tags
  const resolved = c.layers.flatMap((L) => L.controls.map((ctrl) => resolveStatus(L.id, ctrl.name, ctrl.status)));
  const countOf = (s: Stat) => resolved.filter((x) => x === s).length;
  const tally = {
    enforcing: countOf("enforcing") + countOf("hardened"),
    observing: countOf("observing"),
    off: countOf("off"),
    available: countOf("available"),
    attention: countOf("attention") + countOf("exposed"),
  };

  const statusMeta = (s: Stat): { color: string; label: string; icon: React.ReactNode } => {
    switch (s) {
      case "enforcing": case "hardened": return { color: token.colorSuccess, label: s === "hardened" ? "Hardened" : "Enforcing", icon: <CircleCheck size={13} /> };
      case "attention": case "exposed": return { color: token.colorError, label: s === "exposed" ? "Exposed" : "Attention", icon: <CircleAlert size={13} /> };
      case "observing": return { color: token.colorWarning, label: "Observing", icon: <CircleDot size={13} /> };
      case "partial": return { color: token.colorWarning, label: "Partial", icon: <CircleDot size={13} /> };
      case "off": return { color: token.colorTextTertiary, label: "Off", icon: <CircleDot size={13} /> };
      default: return { color: token.colorPrimary, label: "Available", icon: <CircleDot size={13} /> };
    }
  };
  const StatusTag = ({ s }: { s: Stat }) => {
    const m = statusMeta(s);
    return <span className="inline-flex flex-none items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
      style={{ color: m.color, background: `${m.color}14`, border: `1px solid ${m.color}33` }}>{m.icon}{m.label}</span>;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* capability statement — written for an exec; a capability claim, not a guarantee */}
      <div className="rounded-lg border border-border bg-surface-elev px-4 py-3">
        <div className="text-sm text-text-secondary">
          <span className="font-semibold text-text-primary">Multi-layered containment protection for your agents</span>, spanning
          network, filesystem, process, tools, and syscalls. Part of BlueRock's agentic sandbox enforcement capabilities.
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
          {tally.enforcing > 0 && (<><StatusTag s="enforcing" /><span className="text-text-muted">{tally.enforcing} enforcing</span></>)}
          {tally.observing > 0 && (<><StatusTag s="observing" /><span className="text-text-muted">{tally.observing} observing</span></>)}
          {tally.off > 0 && (<><StatusTag s="off" /><span className="text-text-muted">{tally.off} off</span></>)}
          {tally.available > 0 && (<><StatusTag s="available" /><span className="text-text-muted">{tally.available} available</span></>)}
          {tally.attention > 0 && (<><StatusTag s="attention" /><span className="text-text-muted">{tally.attention} need attention</span></>)}
        </div>
      </div>

      {/* layers — hidden while collapsed (header + capability summary stay) */}
      {!collapsed && c.layers.map((L) => {
        const Icon = LAYER_ICON[L.id] ?? ShieldCheck;
        return (
          <Card key={L.id} styles={{ body: { padding: 0 } }}>
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon size={15} className="text-accent" />
                  <span className="text-[15px] font-semibold text-text-primary">{L.name}</span>
                  <Tag style={{ fontFamily: MONO }}>{L.engine}</Tag>
                </div>
                <p className="mt-1 flex max-w-3xl items-start gap-1.5 text-[13px] text-text-secondary">
                  <span>{L.plain}</span>{L.detail && <InfoDot text={L.detail} />}
                </p>
              </div>
            </div>
            <div className="mt-2 border-t border-border">
              {L.controls.map((ctrl, i) => (
                <div key={i} className="flex items-start gap-4 border-b border-border px-4 py-2.5 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-semibold text-text-primary">{ctrl.name}</span>
                      {ctrl.detail && <InfoDot text={ctrl.detail} />}
                    </div>
                    <div className="mt-0.5 text-xs text-text-muted">{ctrl.plain}</div>
                    {imdsBlockToggle(L.id, ctrl.name)}
                  </div>
                  <StatusTag s={resolveStatus(L.id, ctrl.name, ctrl.status)} />
                </div>
              ))}
            </div>
            {/* embedded per-area editor (network, mcp) — the real dedicated surface */}
            {layerEditor(L.id)}
          </Card>
        );
      })}

      {!collapsed && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-text-muted">
          <span className="inline-flex items-center gap-1"><Tooltip title="Active and blocking now"><span className="inline-flex"><StatusTag s="enforcing" /></span></Tooltip></span>
          <span className="inline-flex items-center gap-1"><Tooltip title="Watching and logging, but not blocking"><span className="inline-flex"><StatusTag s="observing" /></span></Tooltip></span>
          <span className="inline-flex items-center gap-1"><Tooltip title="Turned off in this policy"><span className="inline-flex"><StatusTag s="off" /></span></Tooltip></span>
          <span className="inline-flex items-center gap-1"><Popover content={<div style={{ maxWidth: 260 }}>Capability present; not active in this tier/build (e.g. warden not yet in the shipping AMI)</div>}><span className="inline-flex"><StatusTag s="available" /></span></Popover></span>
          <span className="inline-flex items-center gap-1"><Tooltip title="A real exposure to close"><span className="inline-flex"><StatusTag s="attention" /></span></Tooltip></span>
        </div>
      )}
    </div>
  );
}
